/**
 * AuthProvider — Bridge zwischen oidc-client-ts (Authentik) und API-Client
 * (issue #6, ersetzt die Logto-SDK-Anbindung; Muster: Testerwerk
 * AuthentikBridge).
 *
 * 1. Registriert beim API-Client einen Token-Provider, der das Access-Token
 *    aus dem UserManager liefert.
 * 2. Stellt die OIDC-Session beim Start wieder her und spiegelt sie in den
 *    useOidcStore (fuer useAuth/ProtectedRoute).
 * 3. Ruft nach erfolgreichem Login /api/v1/auth/me auf und schreibt das
 *    User-Profil (inkl. Rollen) in den authStore.
 * 4. Bei 401 von /auth/me → GENAU EINMAL signIn() (Loop-Schutz), danach
 *    abmelden + Fehler zeigen.
 * 5. Beim OIDC-Logout den authStore leeren.
 *
 * Ohne OIDC-Konfiguration (lokaler Scaffold) ist der Provider ein No-op.
 */

import { ReactNode, useEffect, useRef } from "react"
import type { User } from "oidc-client-ts"

import { setTokenProvider, setSignInTrigger, apiClient } from "@/lib/api-client"
import { getUserManager, signIn, signOutHard, useOidcStore } from "@/lib/oidc"
import { OIDC_CONFIGURED } from "@/lib/runtime-env"
import { useAuthStore } from "@/store/authStore"

export function AuthProvider({ children }: { children: ReactNode }) {
  const { oidcUser, setOidcUser, setLoading: setOidcLoading } = useOidcStore()
  const { setUser, setTenantId, setLoading, setError, logout } = useAuthStore()
  // Guard against double-fetching in React StrictMode
  const fetchedRef = useRef(false)

  const isAuthenticated = Boolean(oidcUser && !oidcUser.expired)

  // 1a. Token-Provider registrieren
  useEffect(() => {
    if (!OIDC_CONFIGURED) return
    setTokenProvider(async () => {
      try {
        const u = await getUserManager().getUser()
        return u && !u.expired ? u.access_token : undefined
      } catch {
        return undefined
      }
    })
  }, [])

  // 1b. signIn-Trigger im API-Client registrieren (401-Interceptor)
  useEffect(() => {
    if (!OIDC_CONFIGURED) return
    setSignInTrigger(() => {
      signIn()
    })
  }, [])

  // 2. OIDC-Session wiederherstellen + User-Events (Login/Renew/Logout) spiegeln
  useEffect(() => {
    if (!OIDC_CONFIGURED) return
    const um = getUserManager()
    const onLoaded = (u: User) => setOidcUser(u)
    const onUnloaded = () => setOidcUser(null)
    um.events.addUserLoaded(onLoaded)
    um.events.addUserUnloaded(onUnloaded)
    void um.getUser().then((u) => {
      setOidcUser(u && !u.expired ? u : null)
      setOidcLoading(false)
    })
    return () => {
      um.events.removeUserLoaded(onLoaded)
      um.events.removeUserUnloaded(onUnloaded)
    }
  }, [setOidcUser, setOidcLoading])

  // 3. Nach Login: /auth/me → authStore
  useEffect(() => {
    if (!OIDC_CONFIGURED) return
    if (!isAuthenticated) {
      // OIDC-Logout → Store leeren
      if (!fetchedRef.current) return
      fetchedRef.current = false
      logout()
      return
    }

    // Bereits geladen (StrictMode guard)
    if (fetchedRef.current) return
    fetchedRef.current = true

    const fetchMe = async () => {
      setLoading(true)
      try {
        // Display-only Profil-Hints aus dem ID-Token (E-Mail/Name) mitgeben.
        const profile = oidcUser?.profile
        const user = await apiClient.auth.getMe({
          email: profile?.email,
          name: profile?.name ?? profile?.preferred_username,
        })
        // Erfolg → Re-Login-Guard zurücksetzen
        sessionStorage.removeItem("auth_me_retried")
        setUser(user)
        const userWithTenant = user as typeof user & { tenantId?: string }
        if (userWithTenant.tenantId) {
          setTenantId(userWithTenant.tenantId)
        }
      } catch (err: unknown) {
        const status = (err as { status?: number }).status
        if (status === 401 && sessionStorage.getItem("auth_me_retried") !== "1") {
          // Token evtl. abgelaufen → GENAU EINMAL neu anmelden (Loop-Schutz)
          sessionStorage.setItem("auth_me_retried", "1")
          signIn()
        } else if (status === 401) {
          // Zweites 401 trotz frischem Login → KEINE Schleife: abmelden + Fehler zeigen
          sessionStorage.removeItem("auth_me_retried")
          setError("Anmeldung fehlgeschlagen: Profil konnte nicht geladen werden (401). Bitte erneut versuchen.")
          void signOutHard()
        } else {
          setError(err instanceof Error ? err.message : "Profil konnte nicht geladen werden.")
        }
      } finally {
        setLoading(false)
      }
    }

    void fetchMe()
  }, [isAuthenticated, oidcUser, setUser, setTenantId, setLoading, setError, logout])

  return <>{children}</>
}
